import { JSONField, ArrayField, TextField, FieldAffectingData } from 'payload'
import { mergeObjects, sortArrayOfObjects } from '@utils/objects';
import { dateField, rowField } from '@utils/fields';
import { mergeConfigFields } from '@utils/collections';

/**
 * Creates a Observation field for Payload
 *
 * @param config
 * @param withHours
 * @returns
 */
export function observationField(config: Partial<Omit<ArrayField, 'fields'>> & { fields?: WithOneRequired<FieldAffectingData, 'name'>[] }, auditConfig: Partial<JSONField> = {}): [ TextField, ArrayField, JSONField ] {
  const configDefault: CustomPayloadField<ArrayField> = {
    name: 'observations',
    type: 'array',
    label: 'Observação',
    labels: {
      singular: 'Observação',
      plural: 'Observações',
    },
    fields: [],
    admin: {
      className: 'maybe-read-only-text without-actions',
      initCollapsed: true,
      isSortable: false,
      components: {
        RowLabel: '@components/(payload)/fields/array/observation-row-label'
      }
    },
    hooks: {
      beforeChange: [
        ({ value }) => sortArrayOfObjects(value, 'observed_at'),
      ]
    }
  };

  const field: WithRequired<CustomPayloadField<ArrayField>, 'admin'> = mergeObjects(configDefault, config);
  field.admin.className = `${field.admin.className} inline-array-field field-before-hidden-json`;

  field.fields = mergeConfigFields(
    [
      ...rowField([
        ...dateField({
          name: 'observed_at',
          label: 'Data e Hora',
          required: true,
          defaultValue: () => new Date(),
        }, true),
        {
          type: 'text',
          name: 'author',
          label: 'Autor',
          required: true,
          defaultValue: ({user}) => (user?.name || user?.email) || 'Desconhecido',
        },
      ]),
      {
        type: 'textarea',
        name: 'description',
        label: 'Descrição',
        required: true,
        admin: {
          rows: 4,
        }
      },
    ],
    field.fields,
    true
  );

  const fieldName = field.name;

  const emptyField: CustomPayloadField<TextField> = {
    type: 'text',
    name: `${fieldName}_empty_read_only`,
    defaultValue: 'Nenhuma observação encontrada.',
    virtual: true,
    admin: {
      exportable: false,
      condition: (data) => (! data || ! data[fieldName] || ! data[fieldName].length),
      className: 'read-only-text empty-array-text',
      disableListColumn: true,
      disableListFilter: true,
      components: {
        Label: '@components/(payload)/fields/empty',
      }
    },
  };

  const auditDefault: CustomPayloadField<JSONField> = {
    type: 'json',
    name: `${fieldName}_audit`,
    label: 'Auditoria',
    admin: {
      readOnly: true,
      disableListColumn: true,
      disableListFilter: true,
      exportable: false,
      components: {
        Field: {
          path: '@components/(payload)/fields/json/hidden-json',
          clientProps: {
            columns: {
              audited_at: {
                label: 'Data',
                format: 'date:dd/MM/yyyy HH:mm:ss',
              },
              user: {
                label: 'Usuário',
              },
              observations: {
                label: 'Observações',
                columns: {
                  observed_at: {
                    label: 'Data e Hora da Observação',
                    format: 'datetime',
                  },
                  author: {
                    label: 'Autor da Observação',
                  },
                  description: {
                    label: 'Descrição da Observação',
                    format: 'textarea',
                  },
                }
              },
            },
            tableProps: {
              emptyText: 'Nenhum histórico encontrado.'
            }
          }
        }
      }
    },
    hooks: {
      beforeChange: [
        ({ data, value, req }) => {
          if (! data) {
            return value;
          }

          const { user } = req;
          const userId = user?.id || 0;

          value = (! value || ! Array.isArray(value)) ? [] : value;

          const observations = data[fieldName] || [];
          const lastAuditObservations = (value.at(-1) || { observations: [] }).observations;

          if (! lastAuditObservations.length) {
            return [ { user: userId, observations, audited_at: new Date() } ];
          }

          let hasChanges = observations.length !== lastAuditObservations.length;

          for (let i = 0; i < observations.length; i++) {
            // Has changes so we can stop
            if (hasChanges) {
              break;
            }

            const observation = observations[i];
            let observationWasFound = false;

            for (let j = 0; j < lastAuditObservations.length; j++) {
              const record = lastAuditObservations[j];

              if (record.id !== observation.id) {
                continue;
              }

              observationWasFound = true;

              // Check evey value for existing rows
              for (const key in observation) {
                if (observation[key] !== record[key]) {
                  hasChanges = true;
                  break;
                }
              }
            }

            // New observation added
            if (! observationWasFound) {
              hasChanges = true;
              break;
            }
          }

          if (hasChanges) {
            value.push({ user: userId, observations, audited_at: new Date() });
          }

          return value;
        }
      ]
    }
  };

  const auditField = mergeObjects(auditDefault, auditConfig);

  return [ emptyField, field, auditField ];
}
